// src/hooks/useCategories.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CategoryService,
  GlobalCategoryStats,
  PaginatedCategoriesResult,
} from '../admin/pages/category/categoryService';
import { CategoryFormData } from '../enums/product';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
//
// Centralised key factory so every part of the app uses identical keys.
// React Query matches keys by deep equality, so structure matters.
//
//   CATEGORY_KEYS.all                  → invalidates EVERYTHING under 'categories'
//   CATEGORY_KEYS.lists()              → invalidates all paginated list queries
//   CATEGORY_KEYS.paginated(...)       → one specific page + filter combo
//   CATEGORY_KEYS.stats()             → the global stats card data
//
// NOTE: `type` has been removed from the paginated key because the type filter
// no longer exists in the UI. The service always queries main categories and
// fetches subcategories separately.
// ─────────────────────────────────────────────────────────────────────────────
export const CATEGORY_KEYS = {
  all: ['categories'] as const,
  lists: () => ['categories', 'list'] as const,
  paginated: (
    page: number,
    search: string,
    status: string,
    starred: string
  ) => ['categories', 'list', page, search, status, starred] as const,
  stats: () => ['categories', 'stats'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
//
// `type` filter removed — the UI no longer exposes it, and the service always
// queries main categories + subcategories in the hierarchical view.
// `sortOrder` is hardcoded to 0 in the service and is not sent from the UI.
// ─────────────────────────────────────────────────────────────────────────────
export interface CategoryPaginationParams {
  page: number;
  search: string;
  status: string;   // 'all' | 'active' | 'inactive'
  type: string;     // kept for backward compat but always passed as 'all' from component
  starred: string;  // 'all' | 'starred' | 'not-starred'
  pageSize: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// useCategoriesPaginated
//
// Fetches one page of categories from Firestore using offset-based pagination.
//
// The result is cached by (page + search + status + starred).
// Navigating back to the same page/filter combo within the staleTime window
// costs 0 Firestore reads (served from React Query cache).
//
// `placeholderData: (prev) => prev` keeps the previous page visible while the
// next page is loading (no white flash between pages).
// ─────────────────────────────────────────────────────────────────────────────
export const useCategoriesPaginated = (params: CategoryPaginationParams) => {
  return useQuery({
    queryKey: CATEGORY_KEYS.paginated(
      params.page,
      params.search,
      params.status,
      params.starred
      // `type` intentionally excluded from the cache key — always 'all'
    ),
    queryFn: () =>
      CategoryService.getCategoriesPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        // Convert string filter values to the boolean/undefined expected by the service
        params.status !== 'all'
          ? params.status === 'active'
          : undefined,
        // typeFilter argument removed — service no longer accepts it
        params.starred !== 'all'
          ? params.starred === 'starred'
          : undefined
      ),
    staleTime: 1000 * 60 * 3,           // 3 minutes fresh window for admin data
    placeholderData: (prev: any) => prev, // show old data while new page loads
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useCategoryStats
//
// Fetches the global stats counts (main categories, subcategories, starred,
// active, inactive) across ALL documents — independent of pagination or
// active filters.
//
// Uses getCountFromServer under the hood (no document reads, just counts).
// Cached separately from the list with a longer stale time since stats change
// less frequently than the list itself.
// ─────────────────────────────────────────────────────────────────────────────
export const useCategoryStats = () => {
  return useQuery({
    queryKey: CATEGORY_KEYS.stats(),
    queryFn: () => CategoryService.getGlobalStats(),
    staleTime: 1000 * 60 * 5, // 5 minutes — stats update less often
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useCategoryMutations
//
// Returns five mutations: create, update, remove, toggleStatus, toggleStarred.
//
// After every successful mutation, BOTH the paginated list cache and the stats
// cache are invalidated. React Query will refetch them automatically the next
// time the component renders, so the UI always reflects the latest data.
//
// We invalidate CATEGORY_KEYS.all which covers both lists() and stats() in
// one call — any query whose key starts with ['categories'] is invalidated.
// ─────────────────────────────────────────────────────────────────────────────
export const useCategoryMutations = () => {
  const queryClient = useQueryClient();

  // Invalidate all category queries (lists + stats) after any write
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const create = useMutation({
    mutationFn: ({
      formData,
      userId,
      imageFile,
    }: {
      formData: CategoryFormData;
      userId: string;
      imageFile?: File | null;
    }) => CategoryService.createCategory(formData, userId, imageFile),
    onSuccess: invalidateAll,
  });

  // ── Update ────────────────────────────────────────────────────────────────
  const update = useMutation({
    mutationFn: ({
      categoryId,
      formData,
      imageFile,
    }: {
      categoryId: string;
      formData: CategoryFormData;
      imageFile?: File | null;
    }) => CategoryService.updateCategory(categoryId, formData, imageFile),
    onSuccess: invalidateAll,
  });

  // ── Delete ────────────────────────────────────────────────────────────────
  const remove = useMutation({
    mutationFn: (categoryId: string) => CategoryService.deleteCategory(categoryId),
    onSuccess: invalidateAll,
  });

  // ── Toggle Status ─────────────────────────────────────────────────────────
  const toggleStatus = useMutation({
    mutationFn: ({
      categoryId,
      isActive,
    }: {
      categoryId: string;
      isActive: boolean;
    }) => CategoryService.toggleCategoryStatus(categoryId, isActive),
    onSuccess: invalidateAll,
  });

  // ── Toggle Starred ────────────────────────────────────────────────────────
  const toggleStarred = useMutation({
    mutationFn: ({
      categoryId,
      isStarred,
    }: {
      categoryId: string;
      isStarred: boolean;
    }) => CategoryService.toggleCategoryStarred(categoryId, isStarred),
    onSuccess: invalidateAll,
  });

  return { create, update, remove, toggleStatus, toggleStarred };
};